import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress } from '@mui/material';
import SyncWizard from 'components/SyncWizard';
import PageHeader from 'components/PageHeader';
import buildRoute from 'components/buildRoute';
import { deleteRecord, fetchRecord, patchRecord, postAction } from '../registryApi';
import { EXPERIMENT_STATUS, FILE_KIND } from '../registryChoices';
import RegistryBreadcrumbs from '../components/RegistryBreadcrumbs';
import { forgetStep, rememberStep } from './draftProgress';
import { duplicateLabelIds } from './treatmentChecks';
import Step1General from './steps/Step1General';
import Step2Electoral from './steps/Step2Electoral';
import Step3Experiment from './steps/Step3Experiment';
import Step4Treatments from './steps/Step4Treatments';
import Step5Outcomes from './steps/Step5Outcomes';
import Step6WrapUp from './steps/Step6WrapUp';

const API = '/registry/my-experiments';
const TREATMENTS_API = '/registry/treatments';
const TREATMENT_FIELDS = [
  'treatment_label',
  'treatment_description',
  'modes',
  'initial_n',
  'treatment_start_date',
  'treatment_end_date',
];
const STEP_URLS = ['step1', 'step2', 'step3', 'step4', 'step5', 'step6'];
const STEP_LABELS = [
  'General information',
  'Electoral context',
  'The experiment',
  'The treatments',
  'Outcomes and analysis',
  'Wrap-up',
];

const emptyFormData = () => ({
  experiment_name: '',
  partners: [],
  election_type: '',
  election_date: '',
  year: [],
  office_top_ballot: [],
  program_goal: 'gotv',
  states: [],
  election_geo_target: '',
  election_geo_target_other: '',
  funding_status: '',
  political_org_type_527: '',
  experiment_description: '',
  research_questions: [],
  targeting_criteria: '',
  randomization_description: '',
  clustered: null,
  cluster_level: '',
  stopping_rules: '',
  num_treatments: '',
  reference_group: [],
  outcome_data_source: '',
  outcome_data_source_other: '',
  program_start_timing: '',
  program_end_timing: '',
  total_n: '',
  notes: '',
  embargo_period: 'after_election',
  post_embargo_access: 'community',
  anonymous: null,
});

const FORM_FIELDS = Object.keys(emptyFormData());
const NULLABLE_FIELDS = ['election_date', 'program_start_timing', 'program_end_timing', 'num_treatments', 'total_n'];

// The form keeps inputs as strings; the API wants null for empty dates/numbers and
// no blank research-question rows.
const toPayload = (formData, outcome) => {
  const payload = { ...formData };
  NULLABLE_FIELDS.forEach((field) => {
    if (payload[field] === '') payload[field] = null;
  });
  payload.research_questions = formData.research_questions.map((q) => q.trim()).filter(Boolean);
  // Only send outcomes when we actually have one. Sending an empty list would make the
  // serializer delete the auto-created Turnout outcome; omitting the key leaves it alone.
  if (outcome) payload.outcomes = [outcome];
  return payload;
};

// The backend's missing_required_fields() names → the page and question to point people at.
const MISSING_FIELD_LABELS = {
  experiment_name: ['General information', 'experiment name'],
  election_type: ['Electoral context', 'election type'],
  election_date: ['Electoral context', 'election date'],
  program_goal: ['Electoral context', 'program goal'],
  states: ['Electoral context', 'states'],
  funding_status: ['Electoral context', 'funding status'],
  election_geo_target_other: ['Electoral context', 'geographic unit (write-in)'],
  experiment_description: ['The experiment', 'program description'],
  randomization_description: ['The experiment', 'how units are allocated'],
  clustered: ['The experiment', 'whether randomization is clustered'],
  cluster_level: ['The experiment', 'clustering variable'],
  num_treatments: ['The experiment', 'number of treatments'],
  reference_group: ['The experiment', 'reference group'],
  outcome_data_source: ['The experiment', 'outcome data source'],
  outcome_data_source_other: ['The experiment', 'outcome data source (write-in)'],
  data_link: ['The experiment', 'unique identifier data file'],
  treatments: ['The treatments', 'treatment arms'],
  outcomes: ['Outcomes and analysis', 'outcome'],
  embargo_period: ['Wrap-up', 'embargo'],
  post_embargo_access: ['Wrap-up', 'who has access'],
  anonymous: ['Wrap-up', 'anonymity'],
};
const TREATMENT_FIELD_LABELS = {
  treatment_label: 'label',
  treatment_description: 'description',
  modes: 'contact modes',
};

const describeMissingField = (field) => {
  const arm = field.match(/^treatments\[(\d+)\]\.(\w+)$/);
  if (arm) return `The treatments: Treatment ${arm[1]} ${TREATMENT_FIELD_LABELS[arm[2]] || arm[2]}`;
  const doc = field.match(/^additional_docs\[(.+)\]\.document_category$/);
  if (doc) return `The experiment: document type for ${doc[1]}`;
  if (/^outcomes\[.+\]\.regression_model_other$/.test(field)) {
    return 'Outcomes and analysis: regression model (write-in)';
  }
  const [page, question] = MISSING_FIELD_LABELS[field] || ['', field];
  return page ? `${page}: ${question}` : question;
};

const describeMissing = (message) => {
  const prefix = 'Missing required fields:';
  if (!message.startsWith(prefix)) return message;
  const fields = message.slice(prefix.length).split(',').map((f) => f.trim()).filter(Boolean);
  return `Please answer these required questions before submitting — ${fields.map(describeMissingField).join('; ')}.`;
};

const readError = (e, fallback) => {
  try {
    const parsed = JSON.parse(e.message);
    if (Array.isArray(parsed.error)) return describeMissing(parsed.error.join(' '));
    if (parsed.error) return describeMissing(parsed.error);
    return Object.entries(parsed)
      .map(([field, messages]) => `${field}: ${[].concat(messages).join(' ')}`)
      .join(' ');
  } catch {
    return e.message || fallback;
  }
};

const RegistryWizardPage = () => {
  const { projectId, experimentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // The step is NOT a route param here — each step is its own child route (step1…step6),
  // same structure as the crosstab wizard — so read the active step from the path. Reading
  // it from useParams() leaves it undefined, which pins the wizard on step 1 even as the
  // URL advances.
  const step = STEP_URLS.find((url) => location.pathname.endsWith(`/${url}`)) || STEP_URLS[0];
  const activeStep = Math.max(0, STEP_URLS.indexOf(step));
  const storageKey = `registryFormData_${experimentId}`;

  const [formData, setFormData] = useState(emptyFormData());
  const [outcome, setOutcome] = useState(null);
  const [treatments, setTreatments] = useState([]);
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState(EXPERIMENT_STATUS.DRAFT);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const record = await fetchRecord(API, experimentId);
        if (cancelled) return;
        let draft = null;
        try {
          draft = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
        } catch {
          draft = null;
        }
        const merged = emptyFormData();
        FORM_FIELDS.forEach((field) => {
          merged[field] = draft?.formData?.[field] ?? record[field] ?? merged[field];
        });
        setFormData(merged);
        setOutcome(draft?.outcome ?? record.outcomes?.[0] ?? null);
        setTreatments(record.treatments || []);
        setFiles(record.files || []);
        setStatus(record.status);
      } catch (e) {
        if (cancelled) return;
        // A draft that no longer exists (e.g. it was deleted, or demo data was reseeded
        // while a stale tab was open) shouldn't dead-end the wizard — go back to the list.
        if (String(e.message).includes('(404)')) {
          try {
            sessionStorage.removeItem(storageKey);
          } catch {
            // nothing to clean up
          }
          navigate(`/projects/${projectId}/registry`, { replace: true });
          return;
        }
        setLoadError(e.message || 'Failed to load this experiment.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [experimentId, storageKey, navigate, projectId]);

  useEffect(() => {
    if (loading) return;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({ formData, outcome }));
    } catch {
      // sessionStorage unavailable (private mode / quota) — the final submit still saves.
    }
  }, [formData, outcome, loading, storageKey]);

  useEffect(() => {
    if (
      !loading &&
      status !== EXPERIMENT_STATUS.DRAFT &&
      status !== EXPERIMENT_STATUS.NEEDS_UPDATES
    ) {
      navigate(`/projects/${projectId}/registry/${experimentId}`, { replace: true });
    }
  }, [loading, status, projectId, experimentId, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
    rememberStep(experimentId, STEP_URLS[activeStep]);
  }, [activeStep, experimentId]);

  const setField = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));
  const goToStep = (index) => navigate(`/projects/${projectId}/registry/${experimentId}/edit/${STEP_URLS[index]}`);

  // Per-page checks for the doc's required (*) questions on that page — enough to block
  // "Next" with inline errors. The backend's missing_required_fields() is the final word
  // at submit.
  const hasFile = (kind) => files.some((f) => f.kind === kind);
  const validators = [
    () => formData.experiment_name.trim() !== '',
    () =>
      formData.election_type &&
      formData.election_date &&
      formData.states.length > 0 &&
      formData.funding_status &&
      (formData.election_geo_target !== 'other' || formData.election_geo_target_other.trim()),
    () =>
      formData.experiment_description.trim() &&
      formData.randomization_description.trim() &&
      formData.clustered !== null &&
      (!formData.clustered || formData.cluster_level.trim()) &&
      formData.num_treatments >= 1 &&
      formData.reference_group.length > 0 &&
      formData.outcome_data_source &&
      (formData.outcome_data_source !== 'other' || formData.outcome_data_source_other.trim()) &&
      files.every((f) => f.kind !== FILE_KIND.ADDITIONAL_DOC || f.document_category) &&
      hasFile(FILE_KIND.DATA),
    () =>
      treatments.every(
        (t) => t.is_control || (t.treatment_label.trim() && t.treatment_description.trim() && t.modes.length > 0)
      ) && duplicateLabelIds(treatments).size === 0,
    () => !outcome || outcome.regression_model !== 'other' || outcome.regression_model_other.trim(),
    () => formData.post_embargo_access === 'none' || formData.anonymous !== null,
  ];

  const blockIfInvalid = (index) => {
    if (!validators[index]()) {
      setShowErrors(true);
      setSubmitError(
        index === 3 && duplicateLabelIds(treatments).size > 0
          ? 'Two treatments have the same label. Please give each treatment its own label.'
          : 'Please answer the required questions highlighted below.'
      );
      window.scrollTo(0, 0);
      return true;
    }
    setShowErrors(false);
    setSubmitError('');
    return false;
  };

  // Persist the whole form to the database so a draft survives closing the tab or
  // switching machines (sessionStorage alone is per-tab and lost on close). The server
  // keeps treatments/files read-only here, so this never clobbers arms or uploads; it
  // also returns the synced treatment rows Step 4 needs once num_treatments is set.
  const saveDraft = async () => {
    const updated = await patchRecord(API, experimentId, toPayload(formData, outcome));
    setTreatments(updated.treatments || []);
    setFiles(updated.files || []);
    setOutcome(updated.outcomes?.[0] ?? outcome);
    return updated;
  };

  // The server checks the data file's ID column against the outcome source, so that answer
  // must be saved before the upload — not only when the user leaves page 3.
  const saveOutcomeSource = () =>
    patchRecord(API, experimentId, {
      outcome_data_source: formData.outcome_data_source,
      outcome_data_source_other: formData.outcome_data_source_other,
    });

  // Treatment fields save individually on blur, and the submit check reads the server. Save
  // every arm before checking the page, so what's on screen and what's saved can't differ.
  const saveTreatments = () =>
    Promise.all(
      treatments.map((t) =>
        patchRecord(TREATMENTS_API, t.id, Object.fromEntries(TREATMENT_FIELDS.map((f) => [f, t[f]])))
      )
    );

  // The server deletes the arm, renumbers the rest and lowers num_treatments. Reload from it
  // rather than patching local state, so the count here can't go stale and re-create the arm
  // on the next save (the sessionStorage copy of formData follows from setFormData).
  const handleRemoveTreatment = async (treatment) => {
    try {
      await deleteRecord(TREATMENTS_API, treatment.id);
      const record = await fetchRecord(API, experimentId);
      setTreatments(record.treatments || []);
      setFiles(record.files || []);
      setFormData((prev) => ({ ...prev, num_treatments: record.num_treatments ?? '' }));
      setSubmitError('');
    } catch (e) {
      setSubmitError(readError(e, 'Could not remove this treatment.'));
      window.scrollTo(0, 0);
    }
  };

  // The server refuses to lower the count over a treatment that has data. Put the field back
  // to the saved count (so Next still works and reaches page 4, where the treatment can be
  // deleted) and show the server's message. Returns whether the error was that rejection.
  const resetRejectedCount = (e) => {
    let message;
    try {
      [message] = [].concat(JSON.parse(e.message).num_treatments || []);
    } catch {
      return false;
    }
    if (!message) return false;
    setField('num_treatments', treatments.filter((t) => !t.is_control).length);
    setSubmitError(message);
    window.scrollTo(0, 0);
    return true;
  };

  const handleNext = async (index) => {
    if (index === 3) {
      setSaving(true);
      try {
        await saveTreatments();
      } catch (e) {
        setSubmitError(readError(e, 'Could not save the treatments.'));
        setSaving(false);
        return;
      }
      setSaving(false);
    }
    if (blockIfInvalid(index)) return;
    setSaving(true);
    try {
      await saveDraft();
    } catch (e) {
      if (!resetRejectedCount(e)) setSubmitError(readError(e, 'Could not save this page.'));
      return;
    } finally {
      setSaving(false);
    }
    goToStep(index + 1);
  };

  const handleBack = async (index) => {
    setShowErrors(false);
    setSubmitError('');
    // Best-effort save so moving backward doesn't drop what's on the current page.
    // If it fails (e.g. a half-filled field), the sessionStorage copy still has it.
    try {
      await saveDraft();
    } catch (e) {
      // A rejected count is the one failure worth staying for: leaving would keep a count
      // here that the server refused.
      if (resetRejectedCount(e)) return;
    }
    goToStep(index - 1);
  };

  const handleSubmit = async () => {
    if (blockIfInvalid(5)) return;
    setSaving(true);
    try {
      await patchRecord(API, experimentId, toPayload(formData, outcome));
      await postAction(API, experimentId, 'submit');
      try {
        sessionStorage.removeItem(storageKey);
      } catch {
        // nothing to clean up
      }
      forgetStep(experimentId);
      navigate(`/projects/${projectId}/registry/${experimentId}`);
    } catch (e) {
      setSubmitError(readError(e, 'Could not submit this registration.'));
      window.scrollTo(0, 0);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return <Alert severity="error">{loadError}</Alert>;
  }

  const stepProps = (index) => ({
    showErrors,
    onBack: () => handleBack(index),
    onNext: () => handleNext(index),
  });

  const steps = [
    { label: STEP_LABELS[0], content: <Step1General {...stepProps(0)} formData={formData} setField={setField} /> },
    { label: STEP_LABELS[1], content: <Step2Electoral {...stepProps(1)} formData={formData} setField={setField} /> },
    {
      label: STEP_LABELS[2],
      content: (
        <Step3Experiment
          {...stepProps(2)}
          formData={formData}
          setField={setField}
          experimentId={experimentId}
          files={files}
          setFiles={setFiles}
          saving={saving}
          saveOutcomeSource={saveOutcomeSource}
        />
      ),
    },
    {
      label: STEP_LABELS[3],
      content: (
        <Step4Treatments
          {...stepProps(3)}
          experimentId={experimentId}
          numTreatments={formData.num_treatments}
          totalN={formData.total_n}
          treatments={treatments}
          setTreatments={setTreatments}
          onRemoveTreatment={handleRemoveTreatment}
          files={files}
          setFiles={setFiles}
        />
      ),
    },
    { label: STEP_LABELS[4], content: <Step5Outcomes {...stepProps(4)} outcome={outcome} setOutcome={setOutcome} /> },
    {
      label: STEP_LABELS[5],
      content: (
        <Step6WrapUp
          {...stepProps(5)}
          formData={formData}
          setField={setField}
          onSubmit={handleSubmit}
          saving={saving}
        />
      ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <RegistryBreadcrumbs projectId={projectId} current="Register New Experiment" />
      <PageHeader
        title="Register New Experiment"
        subtitle="This experiment will automatically be attributed to your organization."
      />
      {submitError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {submitError}
        </Alert>
      )}
      <Box sx={{ mt: 3 }}>
        <SyncWizard
          steps={steps}
          activeStepIndex={activeStep}
          disableStepClick
          alternativeLabel
          maxWidth={1100}
          navMaxWidth={1100}
          isLoading={saving}
          loadingMessage="Saving…"
        />
      </Box>
    </Box>
  );
};

const routeArgs = {
  element: <RegistryWizardPage />,
};

export default buildRoute(routeArgs);
